import { useAuthContext, useIsGroupAdmin } from 'noddi-web';
import { useIsCurrentUserGroupAnOrganization } from './useIsCurrentUserGroupOrg';

type GroupAdminError = ReturnType<typeof useIsGroupAdmin>['error'];

export type CustomerGroupRole =
  | { kind: 'personal' }
  | { kind: 'organizationAdmin' }
  | { kind: 'organizationMember' }
  | { kind: 'loading' }
  | { kind: 'error'; error: NonNullable<GroupAdminError> };

/**
 * Which customer app the selected group gets. Navigation and Home both read this, so they cannot
 * disagree. The login payload's `isAdmin` answers at once, so the menu does not collapse while the
 * admin lookup runs; the lookup, once settled, wins, which catches a role changed since login.
 * Organization roles fail closed: an errored lookup (even over cached data from an earlier success)
 * is not an admin.
 */
export const useCustomerGroupRole = (): CustomerGroupRole => {
  const isOrgUserGroup = useIsCurrentUserGroupAnOrganization();
  const { currentUserGroupId, userData } = useAuthContext();
  const { isGroupAdmin, isLoading, error } = useIsGroupAdmin(currentUserGroupId, userData?.user.id);

  if (!isOrgUserGroup) {
    return { kind: 'personal' };
  }
  if (error) {
    return { kind: 'error', error };
  }
  const isAdmin =
    isLoading || isGroupAdmin === undefined
      ? userData?.user.userGroups.find((userGroup) => userGroup.id === currentUserGroupId)?.isAdmin
      : isGroupAdmin;
  if (isAdmin === undefined) {
    return { kind: 'loading' };
  }
  return isAdmin ? { kind: 'organizationAdmin' } : { kind: 'organizationMember' };
};
